import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { ILanguage, IProfile, IrMap } from "App/Modules/Models";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { UserProfileIntroDialogDetails } from "./UserProfileIntroDialogDetails";
import { UserProfileIntroDialogLocale } from "./UserProfileIntroDialogLocale";
import type { AvailableIntroTabs } from "./UserProfileIntroDialogTabs";
import { UserProfileIntroDialogTabs } from "./UserProfileIntroDialogTabs";

import "../_user-profile-dialog.scss";
import type { GetDataTransferStatus, SaveDataTransferStatus } from "../../Types";

type UserProfileIntroDialogProps = {
    profile?: IProfile;
    languages: ILanguage[];
    locales: string[];
    timeZones: IrMap<string, string>;
    updateUser: (profile: IProfile) => void;
    getLocalizationData: () => void;
    onFormReset: () => void;
    saveProfileStatus: SaveDataTransferStatus;
    getLocalizationStatus: GetDataTransferStatus;
    initialTab: AvailableIntroTabs;
    onChange: (isDirty: boolean) => void;
    onClose: (event?: any, reason?: "backdropClick" | "escapeKeyDown" | "userCancelled") => void;
};

export const UserProfileIntroDialog = (props: UserProfileIntroDialogProps) => {
    const { initialTab, saveProfileStatus, getLocalizationStatus } = props;
    const [activeTab, setActiveTab] = useState<AvailableIntroTabs>(initialTab || "DetailsTab");
    const [isDirty, setIsDirty] = useState(false);
    const [showRevertInformation, setShowRevertInformation] = useState(false);

    const { t } = useTranslation();

    const handleChangeTab = (tabName: AvailableIntroTabs) => {
        if (isDirty) {
            setShowRevertInformation(true);
        } else {
            setActiveTab(tabName);
            props.onFormReset();
        }
    };

    const handleOnChange = (isFormDirty: boolean) => {
        if (showRevertInformation) {
            setShowRevertInformation(isFormDirty);
        }

        setIsDirty(isFormDirty);
        props.onChange(isFormDirty);
    };

    useEffect(() => {
        props.getLocalizationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {getLocalizationStatus.hasError ? (
                <div className="dialog__section dialog__section--full dialog__section--noscroll">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorLoading")}</p>
                    </div>
                </div>
            ) : (
                <div className="card dialog user-profile-dialog">
                    <div className="dialog__content">
                        <div className="dialog__section dialog__section--full">
                            <h1 className="title title-h1">{t("Gui.Common.UserProfileIntroDialog.MainHeader")}</h1>
                        </div>
                        <div className="dialog__section dialog__section--small">
                            <UserProfileIntroDialogTabs activeTab={activeTab} handleChangeTab={handleChangeTab} />
                        </div>
                        <div className="dialog__section dialog__section--large dialog__section--flipped-spacing">
                            {activeTab === "DetailsTab" ? (
                                <UserProfileIntroDialogDetails
                                    profile={props.profile}
                                    updateUser={props.updateUser}
                                    onFormReset={props.onFormReset}
                                    saveProfileStatus={props.saveProfileStatus}
                                    onChange={handleOnChange}
                                    isDataLoading={props.getLocalizationStatus.isLoading}
                                    showRevertInformation={showRevertInformation}
                                />
                            ) : null}
                            {activeTab === "LocaleTab" ? (
                                <UserProfileIntroDialogLocale
                                    profile={props.profile}
                                    languages={props.languages}
                                    locales={props.locales}
                                    timeZones={props.timeZones}
                                    updateUser={props.updateUser}
                                    onFormReset={props.onFormReset}
                                    saveProfileStatus={props.saveProfileStatus}
                                    getLocalizationStatus={props.getLocalizationStatus}
                                    onChange={handleOnChange}
                                    showRevertInformation={showRevertInformation}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className="dialog__footer">
                        <Button
                            data-testid="close-dialog-button"
                            disabled={saveProfileStatus.isLoading || getLocalizationStatus.isLoading}
                            type="button"
                            variant="text"
                            color="default"
                            size="medium"
                            onClick={(e) => props.onClose(e, "userCancelled")}
                        >
                            {t("Gui.Buttons.Close")}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};
